import React, { useEffect, useRef, useState } from "react";
import { FiUpload } from "react-icons/fi";
import { PropagateLoader } from "react-spinners";
import { toast } from "react-toastify";
import { postApiCaller, postApiCallerFormData } from "../../libs/apiCaller";

const Advertisement = () => {
  const [advertisementActiveTab, setAdvertisementActiveTab] =
    useState("For Consumer");
  const [loading, setLoading] = useState(true);
  const [deleteImageLoading, setDeleteImageLoading] = useState(false);
  const [verticalLoading, setVerticalLoading] = useState(false);
  const [horizontalLoading, setHorizontalLoading] = useState(false);

  const [consumerAds, setConsumerAds] = useState({
    vertical: [],
    horizontal: [],
  });
  const [providerAds, setProviderAds] = useState({
    vertical: [],
    horizontal: [],
  });

  const fileInputRef = useRef(null);
  const horizontalFileInputRef = useRef(null);

  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [horizontalImageIndex, setHorizontalImageIndex] = useState(null);
  const [deleteImageId, setDeleteImageId] = useState(null);

  useEffect(() => {
    handleFetchImages();
  }, []);

  const handleFetchImages = async () => {
    try {
      setLoading(true);
      const response = await postApiCaller("advertisement", {});
      if (response.data.status) {
        const consumerAdsData = response?.data?.response_data?.consumer_ads || {
          vertical: [],
          horizontal: [],
        };
        const providerAdsData = response?.data?.response_data?.provider_ads || {
          vertical: [],
          horizontal: [],
        };

        setConsumerAds(consumerAdsData);
        setProviderAds(providerAdsData);
      } else {
        toast.error("Failed to fetch images.");
      }
    } catch (error) {
      toast.error("Failed to fetch images. Please try again.");
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const generateThumbnail = (file, type) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      video.src = URL.createObjectURL(file);
      video.crossOrigin = "anonymous";
      video.muted = true;
      video.playsInline = true;
      video.currentTime = 2; // Seek to 2 seconds

      // Define canvas size based on type
      const dimensions =
        type === "vertical"
          ? { width: 140, height: 200 }
          : { width: 340, height: 134 };

      // Handle video load
      video.oncanplay = () => {
        video.play(); // Start playing so we can seek properly

        video.onseeked = () => {
          const canvas = document.createElement("canvas");
          canvas.width = dimensions.width;
          canvas.height = dimensions.height;
          const ctx = canvas.getContext("2d");

          ctx.imageSmoothingEnabled = true; // Ensure high-quality scaling

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const timestamp = Date.now();
                const fileName = `I${timestamp}.jpeg`;

                const thumbnailFile = new File([blob], fileName, {
                  type: "image/jpeg",
                });

                resolve(thumbnailFile);
              } else {
                reject(new Error("Failed to create thumbnail blob"));
              }
            },
            "image/jpeg",
            0.9 // Set higher quality
          );

          // Cleanup
          video.pause();
          URL.revokeObjectURL(video.src);
        };

        video.currentTime = 2; // Ensure it seeks again if needed
      };

      video.onerror = () => reject(new Error("Error loading video"));
    });
  };

  const currentAds =
    advertisementActiveTab === "For Consumer" ? consumerAds : providerAds;

  // Handle Image Upload & Replace
  const handleImageUpload = async (event, type) => {
    if (!event.target.files.length) return;

    const file = event.target.files[0];
    if (!file) {
      if (type === "vertical") {
        setVerticalLoading(false);
      } else if (type === "horizontal") {
        setHorizontalLoading(false);
      }
      return;
    }
    // Set the appropriate loading state based on the type
    if (type === "vertical") {
      setVerticalLoading(true);
    } else if (type === "horizontal") {
      setHorizontalLoading(true);
    }

    // Wait for the thumbnail generation to complete
    const thumbnailBlob = await generateThumbnail(file, type);
    const imageUrl = URL.createObjectURL(thumbnailBlob);

    const isVideo = file.type.startsWith("video");
    if (isVideo && file.size > 5 * 1024 * 1024) {
      toast.error("Video size should be less than 5MB.");
      if (type === "vertical") {
        setVerticalLoading(false);
      } else if (type === "horizontal") {
        setHorizontalLoading(false);
      }
      return;
    }

    try {
      const formData = new FormData();

      // Append the type correctly
      const typeValue = advertisementActiveTab === "For Consumer" ? 1 : 2;
      formData.append("type", typeValue);

      // Ensure `type` is a valid string key
      if (type === "horizontal" || type === "vertical") {
        formData.append(type, thumbnailBlob);
        formData.append("video_url", file);
      } else {
        throw new Error("Invalid type. Must be 'horizontal' or 'vertical'");
      }

      // API Request with correct headers
      const response = await postApiCallerFormData(
        "advertisement_store",
        formData
      );

      if (response.data.status) {
        if (advertisementActiveTab === "For Consumer") {
          setConsumerAds((prev) => updateAds(prev, type, imageUrl));
        } else {
          setProviderAds((prev) => updateAds(prev, type, imageUrl));
        }
        handleFetchImages();
        toast.success(response.data.response_message);
      }
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error("Failed to upload image. Please try again.");
    } finally {
      // Reset the loading states based on type
      if (type === "vertical") {
        setVerticalLoading(false);
      } else if (type === "horizontal") {
        setHorizontalLoading(false);
      }
      setLoading(false);
      setSelectedImageIndex(null);
      setHorizontalImageIndex(null);
    }
  };

  const updateAds = (ads, type, imageUrl) => {
    if (type === "vertical" && selectedImageIndex !== null) {
      return {
        ...ads,
        vertical: ads?.vertical?.map((ad, index) =>
          index === selectedImageIndex ? { ...ad, path: imageUrl } : ad
        ),
      };
    } else if (type === "horizontal" && horizontalImageIndex !== null) {
      return {
        ...ads,
        horizontal: ads?.horizontal?.map((ad, index) =>
          index === horizontalImageIndex ? { ...ad, path: imageUrl } : ad
        ),
      };
    } else {
      return {
        ...ads,
        [type]: Array.isArray(ads?.[type])
          ? [...ads[type], { id: Date.now(), path: imageUrl }]
          : [{ id: Date.now(), path: imageUrl }],
      };
    }
  };

  const handleDeleteImage = async (id, type, index) => {
    try {
      setDeleteImageLoading(true);
      const formData = new FormData();

      // Append the type correctly
      formData.append("id", id);

      const response = await postApiCallerFormData(
        "advertisement_delete",
        formData
      );
      if (response.data.status) {
        if (type === "horizontal") {
          if (advertisementActiveTab === "For Consumer") {
            setConsumerAds((prev) => ({
              ...prev,
              horizontal: prev.horizontal.filter((_, i) => i !== index),
            }));
          } else {
            setProviderAds((prev) => ({
              ...prev,
              horizontal: prev.horizontal.filter((_, i) => i !== index),
            }));
          }
        } else {
          if (advertisementActiveTab === "For Consumer") {
            setConsumerAds((prev) => ({
              ...prev,
              vertical: prev.vertical.filter((_, i) => i !== index),
            }));
          } else {
            setProviderAds((prev) => ({
              ...prev,
              vertical: prev.vertical.filter((_, i) => i !== index),
            }));
          }
        }
        toast.success(response.data.response_message);
        handleFetchImages();
        setDeleteImageId(null);
        setDeleteImageLoading(false);
      }
    } catch (error) {
      console.error("Upload Error:", error);
      toast.error("Failed to delete image. Please try again.");
      setDeleteImageLoading(false);
      setDeleteImageId(null);
    }
  };

  return (
    <div className="px-4 md:px-10 mt-4">
      <div className="border border-1 rounded-t-2xl text-xl mt-4 bg-white px-2 py-2">
        <div className="flex justify-between bg-[#F4F9FF] w-full p-1 rounded-xl">
          {["For Consumer", "For Provider"].map((tab) => (
            <button
              key={tab}
              className={`w-full px-4 py-2 text-[#80838D] rounded-lg text-left md:text-center ${
                advertisementActiveTab === tab && "bg-[#284184] text-white"
              }`}
              onClick={() => setAdvertisementActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="text-center text-blue-500 h-[70vh] flex justify-center items-center">
          <PropagateLoader color="#284184" loading={true} size={10} />
        </div>
      ) : (
        <div className="border border-t-0 text-xl bg-white px-3 py-2">
          <h2 className="text-lg font-semibold text-[#284184]">Vertical Ad</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mt-3">
            {currentAds?.vertical?.map((ad, index) => {
              const isVideo = ad?.video_url?.endsWith(".mp4");
              return (
                <div
                  key={ad?.id}
                  className="relative border rounded-lg p-2 shadow-md"
                >
                  {isVideo ? (
                    <video
                      src={ad?.video_url}
                      className="w-full h-[150px] rounded-lg object-cover"
                      controls
                    />
                  ) : (
                    <img
                      src={ad?.path}
                      className="w-full h-[150px] rounded-lg object-cover"
                      alt="Ad"
                    />
                  )}
                  <div className="flex justify-between text-sm mt-2">
                    <button
                      className="text-[#E0A256] text-xl font-bold underline"
                      onClick={() => {
                        setDeleteImageId(ad?.id);
                        handleDeleteImage(ad?.id, "vertical", index);
                      }}
                      disabled={deleteImageId === ad?.id && deleteImageLoading}
                    >
                      {deleteImageId === ad?.id && deleteImageLoading
                        ? "Loading..."
                        : "Delete"}
                    </button>
                    {/* <button
                  className="text-[#284184] text-xl font-bold underline"
                  onClick={() => handleChangeImage("vertical", index)}
                >
                  Change
                </button> */}
                  </div>
                </div>
              );
            })}
            <label className="border-dashed h-[180px] border-2 border-orange-400 rounded-lg flex items-center justify-center p-4 text-center text-orange-500 cursor-pointer">
              {verticalLoading ? (
                "Loading..."
              ) : (
                <>
                  <span className="flex flex-col items-center justify-center">
                    <FiUpload />
                    Upload Image/Video
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg, image/png, video/mp4"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, "vertical")}
                    ref={fileInputRef}
                    disabled={verticalLoading}
                  />
                </>
              )}
            </label>
          </div>
          <hr className="mt-4 mb-2" />
          <h2 className="text-lg font-semibold text-[#284184]">
            Horizontal Ad
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mt-3">
            {currentAds?.horizontal?.map((ad, index) => {
              const isVideo = ad?.video_url?.endsWith(".mp4");
              return (
                <div
                  key={ad?.id}
                  className="relative border rounded-lg p-2 shadow-md"
                >
                  {isVideo ? (
                    <video
                      src={ad?.video_url}
                      className="w-[340px] rounded-lg object-cover h-[110px]"
                      controls
                    />
                  ) : (
                    <img
                      src={ad?.path}
                      className="w-[340px] rounded-lg object-cover h-[110px]"
                      alt="Ad"
                    />
                  )}

                  <div className="flex justify-between text-sm mt-2">
                    <button
                      className="text-[#E0A256] text-xl font-bold underline"
                      onClick={() => {
                        setDeleteImageId(ad?.id);
                        handleDeleteImage(ad?.id, "horizontal", index);
                      }}
                      disabled={deleteImageId === ad?.id && deleteImageLoading}
                    >
                      {deleteImageId === ad?.id && deleteImageLoading
                        ? "Loading..."
                        : "Delete"}
                    </button>
                    {/* <button
                  className="text-[#284184] text-xl font-bold underline"
                  onClick={() => handleChangeImage("horizontal", index)}
                >
                  Change
                </button> */}
                  </div>
                </div>
              );
            })}
            <label className="border-dashed h-[180px] border-2 border-orange-400 rounded-lg flex items-center justify-center p-4 text-center text-orange-500 cursor-pointer">
              {horizontalLoading ? (
                "Loading..."
              ) : (
                <>
                  <span className="flex flex-col items-center justify-center">
                    <FiUpload />
                    Upload Image/Video
                  </span>
                  <input
                    type="file"
                    accept="image/jpeg, image/png, video/mp4"
                    className="hidden"
                    onChange={(e) => handleImageUpload(e, "horizontal")}
                    ref={horizontalFileInputRef}
                    disabled={horizontalLoading}
                  />
                </>
              )}
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default Advertisement;
